import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, BarChart2, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/shadcn/button';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useCampaigns } from '@/entities/campaign';
import { CampaignListSort } from '@/entities/campaign';
import type { Campaign } from '@/entities/campaign';
import { cn } from '@/shared/utils';
import { sunscreenCampaignAnalysis } from '../data/sunscreen-campaign-analysis';
import { AnalysisResults } from '../components/analysis-results';

type AnalysisState = 'select' | 'analyzing' | 'results';

export function CampaignAnalysisPage() {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [analysisState, setAnalysisState] = useState<AnalysisState>('select');

  const campaignsQuery = useCampaigns({ sort: CampaignListSort.ENDING_SOON });
  const campaigns = campaignsQuery.data?.pages.flatMap((p) => p.items) ?? [];

  const toggleCampaign = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 3) {
        next.add(id);
      }
      return next;
    });
  };

  const handleAnalyze = () => {
    if (selected.size === 0) return;
    setAnalysisState('analyzing');
    setTimeout(() => setAnalysisState('results'), 1800);
  };

  const selectedNames = campaigns
    .filter((c) => selected.has(c.id))
    .map((c) => c.name);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-6 py-4">
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          onClick={() =>
            navigate({
              to: '/workspace/$workspaceId/campaigns/',
              params: { workspaceId: workspace.id },
            })
          }
        >
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-2">
          <BarChart2 className="size-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold leading-tight">Campaign Analysis</h1>
            <p className="text-xs text-muted-foreground">
              Identify what drove performance in your campaigns
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6">
        {analysisState === 'select' && (
          <div className="flex flex-col gap-6">
            {/* Instructions */}
            <div className="rounded-xl border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">
                Select up to 3 campaigns to analyze
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                We'll identify the content variables that drove the most engagement and recommend
                your next campaign strategy.
              </p>
            </div>

            {/* Campaign list */}
            {campaignsQuery.isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            ) : campaigns.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <p className="text-sm text-muted-foreground">No campaigns found.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: '/workspace/$workspaceId/campaigns/create',
                      params: { workspaceId: workspace.id },
                    })
                  }
                >
                  Create a campaign
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {campaigns.map((campaign) => (
                  <CampaignSelectItem
                    key={campaign.id}
                    campaign={campaign}
                    isSelected={selected.has(campaign.id)}
                    isDisabled={selected.size >= 3 && !selected.has(campaign.id)}
                    onToggle={() => toggleCampaign(campaign.id)}
                  />
                ))}
              </div>
            )}

            {/* Action */}
            <div className="flex items-center justify-between gap-3 rounded-xl border bg-card p-4">
              <span className="text-sm text-muted-foreground">
                {selected.size === 0
                  ? 'Select at least 1 campaign to analyze'
                  : `${selected.size} campaign${selected.size > 1 ? 's' : ''} selected`}
              </span>
              <Button
                disabled={selected.size === 0}
                onClick={handleAnalyze}
                className="gap-2"
              >
                Analyze
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}

        {analysisState === 'analyzing' && (
          <div className="flex flex-col items-center justify-center gap-4 py-24">
            <Loader2 className="size-10 animate-spin text-primary" />
            <div className="text-center">
              <p className="text-sm font-medium">Analyzing high-performing content…</p>
              <p className="text-xs text-muted-foreground mt-1">
                Identifying content variables and creator patterns
              </p>
            </div>
          </div>
        )}

        {analysisState === 'results' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <button
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => {
                  setAnalysisState('select');
                  setSelected(new Set());
                }}
              >
                <ArrowLeft className="size-3.5" />
                Re-select campaigns
              </button>
            </div>
            <AnalysisResults
              report={sunscreenCampaignAnalysis}
              selectedCampaignNames={selectedNames}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface CampaignSelectItemProps {
  campaign: Campaign;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}

function CampaignSelectItem({
  campaign,
  isSelected,
  isDisabled,
  onToggle,
}: CampaignSelectItemProps) {
  return (
    <button
      onClick={onToggle}
      disabled={isDisabled}
      className={cn(
        'flex items-center gap-3 rounded-xl border p-4 text-left transition-colors',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-card hover:bg-muted/40',
        isDisabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <div
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
          isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40',
        )}
      >
        {isSelected && (
          <svg
            className="size-3 text-primary-foreground"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M2 6l3 3 5-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{campaign.name}</p>
        <p className="text-xs text-muted-foreground capitalize">
          {campaign.status}
          {campaign.platforms && campaign.platforms.length > 0 && ` · ${campaign.platforms.join(', ')}`}
        </p>
      </div>
    </button>
  );
}
